import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiBriefcase, FiChevronDown, FiGrid, FiLogOut, FiSettings, FiUser } from 'react-icons/fi';
import Api from '../../Api';
import './style.css';

export default function Header() {
  const [userName, setUserName] = useState('');
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isCompanyMenuOpen, setIsCompanyMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [crossLink, setCrossLink] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    async function checkLink() {
      try {
        const data = await Api.checkCrossPlatformLink();
        if (data.linked) {
          setCrossLink(data);
        }
      } catch (error) {
        // console.error('Failed to check cross link', error);
      }
    }
    checkLink();
  }, []);

  function handleCrossPlatformRedirect() {
    if (!crossLink) return;

    const { targetUrl, params } = crossLink;
    
    // Webapp uses simple params in login.html
    const queryString = new URLSearchParams(params).toString();
    const url = `https://${targetUrl}/login.html?${queryString}`;
    
    window.location.href = url;
  }

  useEffect(() => {
    try {
      const storedUser = localStorage.getItem('authUser');
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        if (parsedUser && parsedUser.name) {
          const firstName = parsedUser.name.split(' ')[0];
          setUserName(firstName);
        }
      }
    } catch (error) {
      setUserName('');
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    async function loadCompanies() {
      try {
        const data = await Api.getCompanies();
        if (!isMounted) {
          return;
        }

        if (Array.isArray(data)) {
          setCompanies(data);

          let initialId = '';
          let initialCompany = null;
          const storedCompany = localStorage.getItem('selectedCompany');

          if (storedCompany) {
            try {
              const parsedCompany = JSON.parse(storedCompany);
              if (parsedCompany && parsedCompany.id) {
                const exists = data.find(
                  company => String(company.id) === String(parsedCompany.id),
                );

                if (exists) {
                  initialId = String(parsedCompany.id);
                  initialCompany = exists;
                }
              }
            } catch (error) {
              initialId = '';
            }
          }

          if (!initialId && data.length > 0) {
            const firstCompany = data[0];
            initialId = String(firstCompany.id);
            initialCompany = firstCompany;
            localStorage.setItem('selectedCompany', JSON.stringify(firstCompany));
          }

          setSelectedCompanyId(initialId);
          setSelectedCompany(initialCompany);
        }
      } catch (error) {
        setCompanies([]);
      }
    }

    loadCompanies();

    return () => {
      isMounted = false;
    };
  }, []);

  function handleLogout() {
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
    navigate('/login');
  }

  function handleCompanySelect(company) {
    const newCompanyId = String(company.id);
    setSelectedCompanyId(newCompanyId);
    setSelectedCompany(company);
    setIsCompanyMenuOpen(false);

    localStorage.setItem('selectedCompany', JSON.stringify(company));

    // Dispatch custom event for Dashboard to update
    window.dispatchEvent(new Event('companyChange'));

    // Reload the page to update data for the new company while staying on the same page
    window.location.reload();
  }

  function getCompanyLogoUrl(company) {
    if (!company || !company.logo) {
      return null;
    }

    const logo = company.logo;

    if (logo && logo.type === 'Buffer' && logo.data) {
      const base64String = btoa(
        new Uint8Array(logo.data).reduce(
          (data, byte) => data + String.fromCharCode(byte),
          ''
        )
      );

      return `data:image/png;base64,${base64String}`;
    }

    if (typeof logo === 'string') {
      return logo;
    }

    return null;
  }

  function isActive(path) {
    if (path === '/') {
      return location.pathname === '/';
    }

    return location.pathname.startsWith(path);
  }

  const isMobile = window.innerWidth <= 768;

  return (
    <header className="app-header">
      <div className="app-header-left">
        <button
          type="button"
          className="app-header-logo"
          onClick={() => navigate('/')}
        >
          <span className="app-header-mark">
            <img src="../logo-white.png" alt="ClockFy" className="app-header-logo-image" style={{width: '22px'}}/>
          </span>
          <div className="app-header-brand">
            <span className="app-header-title">ClockFy</span>
            <span className="app-header-subtitle">Gestão Inteligente</span>
          </div>
        </button>
      </div>

      <div className="app-header-right">
        {crossLink && isMobile && (
          <button
            type="button"
            className="app-header-action"
            onClick={handleCrossPlatformRedirect}
            title="Ir para o Aplicativo"
            style={{
              marginRight: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              background: 'var(--primary-color)',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              padding: '0.5rem 1rem',
              fontSize: '0.875rem',
              cursor: 'pointer',
              fontWeight: 500
            }}
          >
            <FiGrid size={16} />
            <span>App</span>
          </button>
        )}
        {companies.length > 0 && selectedCompany && (
          <div className="app-header-company" style={{display: 'none'}}>
            <button
              type="button"
              className="app-header-company-trigger"
              onClick={() => setIsCompanyMenuOpen(prev => !prev)}
            >
              <div className="app-header-company-avatar">
                {getCompanyLogoUrl(selectedCompany) ? (
                  <img
                    src={getCompanyLogoUrl(selectedCompany)}
                    alt={selectedCompany.corporate_name}
                  />
                ) : (
                  <span>
                    {(selectedCompany.trade_name || selectedCompany.corporate_name || '?')
                      .charAt(0)
                      .toUpperCase()}
                  </span>
                )}
              </div>
              <div className="app-header-company-info">
                <span className="app-header-company-name">
                  {selectedCompany.trade_name || selectedCompany.corporate_name}
                </span>
                {selectedCompany.cnpj && (
                  <span className="app-header-company-meta">
                    CNPJ: {selectedCompany.cnpj}
                  </span>
                )}
              </div>
              <div className={`app-header-company-chevron ${isCompanyMenuOpen ? 'open' : ''}`}>
                <FiChevronDown size={14} />
              </div>
            </button>

            {isCompanyMenuOpen && (
              <div className="app-header-company-menu">
                {companies.map(company => {
                  const logoUrl = getCompanyLogoUrl(company);
                  const isActive = String(company.id) === selectedCompanyId;

                  return (
                    <button
                      key={company.id}
                      type="button"
                      className={`app-header-company-option ${isActive ? 'active' : ''}`}
                      onClick={() => handleCompanySelect(company)}
                    >
                      <div className="app-header-company-option-avatar">
                        {logoUrl ? (
                          <img src={logoUrl} alt={company.corporate_name} />
                        ) : (
                          <span>
                            {(company.trade_name || company.corporate_name || '?')
                              .charAt(0)
                              .toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div className="app-header-company-option-info">
                        <span className="app-header-company-option-name">
                          {company.trade_name || company.corporate_name}
                        </span>
                        {company.cnpj && (
                          <span className="app-header-company-option-meta">
                            CNPJ: {company.cnpj}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <div className="app-header-user">
          <button
            type="button"
            className="app-header-user-trigger"
            onClick={() => setIsUserMenuOpen(prev => !prev)}
          >
            <div className="app-header-avatar">
              {userName ? (
                <span>{userName.charAt(0).toUpperCase()}</span>
              ) : (
                <FiUser size={16} />
              )}
            </div>
            <div className="app-header-user-info">
              <span className="app-header-user-name">
                {userName || 'Usuário'}
              </span>
            </div>
            <div className={`app-header-user-chevron ${isUserMenuOpen ? 'open' : ''}`}>
              <FiChevronDown size={14} />
            </div>
          </button>

          {isUserMenuOpen && (
            <div className="app-header-user-menu">
              <button
                type="button"
                className="app-header-user-option"
                onClick={handleLogout}
              >
                <FiLogOut size={14} />
                <span>Sair</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
